import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../../lib/api";
import RichTextEditor from "../../components/admin/RichTextEditor";
import ImageUpload from "../../components/admin/ImageUpload";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
    title: "",
    date: new Date().toISOString().slice(0, 10),
    cover_image_url: "",
    excerpt: "",
    body_html: "",
    published: true,
};

export default function BlogAdmin() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null); // null closed, {} new/edit
    const [form, setForm] = useState(empty);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => api.get("/admin/blog").then((r) => setPosts(r.data)), []);
    useEffect(() => {
        load();
    }, [load]);

    const openNew = () => {
        setForm(empty);
        setEditing({ id: null });
        setError("");
    };
    const openEdit = (p) => {
        setForm({
            title: p.title,
            date: p.date,
            cover_image_url: p.cover_image_url || "",
            excerpt: p.excerpt || "",
            body_html: p.body_html || "",
            published: p.published,
        });
        setEditing(p);
        setError("");
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            if (editing.id) {
                await api.put(`/admin/blog/${editing.id}`, form);
            } else {
                await api.post("/admin/blog", form);
            }
            setEditing(null);
            await load();
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || "Could not save");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"?`)) return;
        await api.delete(`/admin/blog/${p.id}`);
        load();
    };

    const set = (k) => (e) =>
        setForm({ ...form, [k]: e && e.target ? e.target.value : e });

    return (
        <div data-testid="admin-blog">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-serif text-3xl text-blue-50">Blog</h1>
                    <p className="mt-1 text-sm text-slate-300/55">
                        Notes on play, feelings, and family life.
                    </p>
                </div>
                <button
                    data-testid="blog-new-button"
                    onClick={openNew}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-5 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110"
                >
                    <Plus className="h-4 w-4" /> New post
                </button>
            </div>

            <div className="mt-8 space-y-3">
                {posts.length === 0 && (
                    <p className="text-sm text-slate-300/45">No posts yet.</p>
                )}
                {posts.map((p) => (
                    <div
                        key={p.id}
                        className="flex items-center gap-4 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-4"
                    >
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-serif text-lg text-slate-300">
                                    {p.title}
                                </h3>
                                {!p.published && (
                                    <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-sky-400">
                                        Draft
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-300/45">{p.date}</p>
                        </div>
                        <button
                            onClick={() => openEdit(p)}
                            aria-label="Edit"
                            data-testid={`blog-edit-${p.id}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/80 hover:bg-blue-500/15"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => remove(p)}
                            aria-label="Delete"
                            data-testid={`blog-delete-${p.id}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-red-300/80 hover:bg-red-500/15"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-6">
                    <form
                        onSubmit={save}
                        className="my-6 w-full max-w-2xl rounded-2xl border border-blue-500/20 bg-[#0c172e] p-7"
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="font-serif text-2xl text-blue-50">
                                {editing.id ? "Edit post" : "New post"}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/70 hover:bg-blue-500/15"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="mt-6 space-y-5">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                        Title
                                    </label>
                                    <input
                                        data-testid="blog-title-input"
                                        required
                                        value={form.title}
                                        onChange={set("title")}
                                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                        Date
                                    </label>
                                    <input
                                        data-testid="blog-date-input"
                                        type="date"
                                        required
                                        value={form.date}
                                        onChange={set("date")}
                                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                    />
                                </div>
                            </div>

                            <ImageUpload
                                label="Cover image"
                                value={form.cover_image_url}
                                onChange={(url) => setForm({ ...form, cover_image_url: url })}
                            />

                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Excerpt
                                </label>
                                <input
                                    data-testid="blog-excerpt-input"
                                    value={form.excerpt}
                                    onChange={set("excerpt")}
                                    placeholder="One-line summary shown on the blog list"
                                    className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Body
                                </label>
                                <RichTextEditor
                                    value={form.body_html}
                                    onChange={(html) => setForm((f) => ({ ...f, body_html: html }))}
                                />
                            </div>

                            <label className="flex items-center gap-2 text-sm text-slate-300/75">
                                <input
                                    data-testid="blog-published-toggle"
                                    type="checkbox"
                                    checked={form.published}
                                    onChange={(e) =>
                                        setForm({ ...form, published: e.target.checked })
                                    }
                                    className="h-4 w-4 accent-blue-500"
                                />
                                Published (visible on the public site)
                            </label>

                            {error && <p className="text-sm text-red-400">{error}</p>}
                        </div>

                        <div className="mt-7 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="rounded-full border border-blue-500/30 px-6 py-2.5 text-sm text-slate-300 hover:bg-blue-500/10"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="blog-save-button"
                                type="submit"
                                disabled={busy}
                                className="rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-6 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110 disabled:opacity-60"
                            >
                                {busy ? "Saving…" : "Save post"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
